/**
 * Normalisation des cellules Excel — partagée volontairement.
 *
 * L'aperçu d'import doit montrer **exactement** ce que le serveur écrira :
 * deux implémentations qui divergeraient d'un centime ou d'un jour rendraient
 * l'écran de contrôle trompeur. Ces fonctions sont donc l'unique référence,
 * utilisées par l'aperçu comme par le service d'import.
 */

/**
 * Ramène une date de feuille vers `YYYY-MM-DD`.
 * Accepte `12/03/2025`, `12-03-2025`, `2025-03-12` et le numéro de série Excel.
 * Renvoie null si rien d'exploitable — l'appelant marque alors `a_verifier`.
 */
export function normaliserDate(brut: string | null | undefined): string | null {
  if (!brut) return null;
  const v = String(brut).trim();
  if (v === '') return null;

  // Déjà au bon format.
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  // jj/mm/aaaa ou jj-mm-aaaa : la convention française de la feuille.
  const fr = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/.exec(v);
  if (fr) {
    const jour = (fr[1] ?? '').padStart(2, '0');
    const mois = (fr[2] ?? '').padStart(2, '0');
    let annee = fr[3] ?? '';
    if (annee.length === 2) annee = `20${annee}`;

    const m = Number(mois);
    const j = Number(jour);
    if (m >= 1 && m <= 12 && j >= 1 && j <= 31) return `${annee}-${mois}-${jour}`;
    return null;
  }

  // Numéro de série Excel (jours depuis le 30/12/1899).
  const serie = Number(v);
  if (Number.isFinite(serie) && serie > 20000 && serie < 60000) {
    const base = Date.UTC(1899, 11, 30);
    return new Date(base + serie * 86_400_000).toISOString().slice(0, 10);
  }

  return null;
}

/** Analyse un montant écrit à la main : « 1 250,50 », « 1250.5 », « 750 DH ». */
export function normaliserMontant(brut: string | number | null | undefined): number | null {
  if (brut === null || brut === undefined) return null;
  if (typeof brut === 'number') return Number.isFinite(brut) ? brut : null;

  const v = brut.replace(/[^\d,.\-+]/g, '').replace(/\s/g, '');
  if (v === '' || v === '-' || v === '+') return null;

  // Une virgule est toujours décimale ; le point aussi quand il est seul.
  const normalise = v.includes(',') ? v.replace(/\./g, '').replace(',', '.') : v;
  const n = Number(normalise);
  return Number.isFinite(n) ? n : null;
}
